import { useQuery, useMutation } from '@apollo/react-hooks';
import { useEffect } from 'react';

import type {
	ContentPermissionsQuery_usersWithContentRestrictions as UsersContentPermissions,
	ContentPermissionsQuery_contentPermissions_subjectsWithEffectiveContentPermissions_nodes as User,
} from '../graphql/__types__/UsersContentPermissionsQuery.js';
import { UpdateContentPermissionsMutation } from '../graphql/UpdateContentPermissionsMutation.graphql';
import { UsersContentPermissionsQuery } from '../graphql/UsersContentPermissionsQuery.graphql';

import { useMentionsNotifications, type MentionType } from './useMentionsNotifications';

type ContentPermissionsQueryProps = {
	contentId: string;
	isUpdatedMentionsInLivePagesEnabled: boolean;
	currentUserId: string | null;
	mentionsToDisplay: MentionType[];
};

export const useUsersContentPermissions = ({
	contentId,
	isUpdatedMentionsInLivePagesEnabled,
	currentUserId,
	mentionsToDisplay,
}: ContentPermissionsQueryProps) => {
	const mentionedUserIds = mentionsToDisplay.map((mention) => mention.id);
	//TODO: save the permissions of users in a separate state if it's already available, so that we don't have to query forit again
	const allUniqueMentionedUserIds = new Set(mentionedUserIds);
	const {
		data: contentPermissionsData,
		error: usersContentPermissionsError,
		loading: usersContentPermissionsLoading,
	} = useQuery(UsersContentPermissionsQuery, {
		variables: { contentId, accountIds: Array.from(allUniqueMentionedUserIds) },
		skip: !isUpdatedMentionsInLivePagesEnabled || mentionsToDisplay?.length === 0,
		fetchPolicy: 'network-only', // we'll need to check user's permissions everytime to correctly display the correct flag contents in case the user's permission changed
	});
	const [
		{},
		{
			setAddedMentions,
			setAddedTasks,
			addUnrestrictedMentions,
			addParentSpaceRestrictedMentions,
			addPageRestrictedMentions,
			addParentSpaceRestrictedTasks,
			addPageRestrictedTasks,
			addUnrestrictedTasks,
		},
	] = useMentionsNotifications();

	const [updateUsersContentPermissionMutation] = useMutation(UpdateContentPermissionsMutation);

	const mentionsWithSpaceRestrictions: MentionType[] = [];
	const usersWithSpaceRestrictionsIds = new Set<string>();
	const mentionsWithParentPageRestrictions: MentionType[] = [];
	const usersWithParentPageRestrictionsIds = new Set<string>();
	const usersWithSpaceOrParentRestrictionsIds = new Set<string>();
	const usersWithCurrentPageRestrictionsIds = new Set<string>();
	const usersWithUnrestrictedPageAccessIds = new Set<string>();

	const accountIdToDisplayNameMap = new Map();

	useEffect(() => {
		if (contentPermissionsData && mentionsToDisplay?.length) {
			const { contentPermissions, usersWithContentRestrictions: contentRestrictions } =
				contentPermissionsData;
			if (contentPermissions?.contentAccess === 'EVERYONE_NO_ACCESS') {
				contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes?.forEach(
					(user: User) => {
						if (user.id === currentUserId || !user.id) {
							return;
						}
						usersWithUnrestrictedPageAccessIds.add(user.id);
						accountIdToDisplayNameMap.set(user.id, {
							displayName: user.displayName,
							userPhoto: user?.user?.profilePicture?.path,
						});
					},
				);
			}
			contentRestrictions?.forEach((user: UsersContentPermissions) => {
				if (!user.accountId) {
					return;
				}
				accountIdToDisplayNameMap.set(user.accountId, {
					displayName: user.displayName,
					userPhoto: user?.profilePicture?.path,
				});
				if (!user.hasSpaceViewPermission) {
					//has space level restriction
					usersWithSpaceRestrictionsIds.add(user.accountId);
					usersWithSpaceOrParentRestrictionsIds.add(user.accountId);
				} else if (user.restrictingContent?.hasRestrictions) {
					//has parent page level restriction
					usersWithParentPageRestrictionsIds.add(user.accountId);
					usersWithSpaceOrParentRestrictionsIds.add(user.accountId);
				} else if (contentPermissions?.contentAccess === 'EVERYONE_NO_ACCESS') {
					//might have page level restriction
					if (!usersWithUnrestrictedPageAccessIds.has(user.accountId)) {
						usersWithCurrentPageRestrictionsIds.add(user.accountId);
					}
				}
			});

			mentionsToDisplay.forEach((mention) => {
				const mentionToAdd = {
					...mention,
					displayName: accountIdToDisplayNameMap.get(mention.id)?.displayName ?? '',
					userPhoto: accountIdToDisplayNameMap.get(mention.id)?.userPhoto ?? '',
				};
				if (mention?.taskLocalId) {
					//Buckets added task mentions into different permission groups
					if (usersWithSpaceRestrictionsIds.has(mention.id)) {
						//Users who dont have access to the page due to space restrictions
						void addParentSpaceRestrictedTasks([mentionToAdd]);
					} else if (usersWithParentPageRestrictionsIds.has(mention.id)) {
						//Users who don't have access to the page due to parent page restrictions
						void addParentSpaceRestrictedTasks([mentionToAdd]);
					} else if (usersWithCurrentPageRestrictionsIds.has(mention.id)) {
						//Users who don't have access to the page due to current page restrictions
						void addPageRestrictedTasks([mentionToAdd]);
					} else {
						//Users who have access to the page
						void addUnrestrictedTasks([mentionToAdd]);
					}
				} else {
					//Buckets added general mentions into different permission groups
					if (usersWithSpaceRestrictionsIds.has(mention.id)) {
						//Users who dont have access to the page due to space restrictions
						mentionsWithSpaceRestrictions.push(mentionToAdd);
						void addParentSpaceRestrictedMentions([mentionToAdd]);
					} else if (usersWithParentPageRestrictionsIds.has(mention.id)) {
						//Users who don't have access to the page due to parent page restrictions
						mentionsWithParentPageRestrictions.push(mentionToAdd);
						void addParentSpaceRestrictedMentions([mentionToAdd]);
					} else if (usersWithCurrentPageRestrictionsIds.has(mention.id)) {
						//Users who don't have access to the page due to current page restrictions
						void addPageRestrictedMentions([mentionToAdd]);
					} else {
						//Users who have access to the page
						void addUnrestrictedMentions([mentionToAdd]);
					}
				}
			});

			void setAddedMentions([]);
			void setAddedTasks([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentPermissionsData]);

	return {
		usersContentPermissionsError,
		usersContentPermissionsLoading,
		updateUsersContentPermissionMutation,
		allUniqueMentionedUserIds,
		usersWithSpaceRestrictionsIds,
		usersWithParentPageRestrictionsIds,
		usersWithUnrestrictedPageAccessIds,
		usersWithCurrentPageRestrictionsIds,
		usersWithSpaceOrParentRestrictionsIds,
	};
};
